import { TEST_IDS } from '@va/constants';
import { isCountryBasedPrivacyModeEnabled } from '@va/dashboard/selectors/app';
import { FolderIcon, LocationIcon, RadioInputSelected } from '@va/icons';
import { useTranslate } from '@va/localization';
import { contributorRole } from '@va/standalone/shared/selectors';
import { PrivacyLevelsEnum } from '@va/standalone/shared/types';
import {
  DisabledOverlay,
  IconWithBackground,
  OptionStatusEnum,
  Paragraph,
  SelectDropdownWithGroups,
  SelectDropdownWithGroupsOptionType,
  fontWeights,
  paragraphSizes,
} from '@va/ui/design-system';
import { TooltipWrapper } from '@va/ui/tooltips';
import { is3asApp, isWixMiniApp } from '@va/util/helpers';
import classNames from 'classnames';
import { ReactNode, useMemo } from 'react';
import { useSelector } from 'react-redux';
import PrivacyLevelCardWrapper from './PrivacyLevelCardWrapper';
import PrivacyLevelFeatureCard from './PrivacyLevelFeatureCard';
import { AaasMinCreditsTooltip, UpgradePlanTooltip } from './UpgradePlanTooltip';
import './pulse-effect.scss';
import usePrivacyLevelFeatures from './usePrivacyLevelFeatures';

type DataPrivacyCardProps = {
  handlePrivacyLevelClick: (value: PrivacyLevelsEnum) => void;
  onHover?: () => void;
  selected: boolean;
  value: PrivacyLevelsEnum;
  baseKey: string;
  options: SelectDropdownWithGroupsOptionType[];
  handleOnOptionClick: (privacyLevel: PrivacyLevelsEnum, countryCode: string) => void;
  variant?: 'default' | 'wix-mini';
  disabled?: boolean;
  expanded?: boolean;
  disabledTooltip?: ReactNode;
};

const styles = {
  default: {
    countriesDropdown: {
      bgColorClass: '!bg-white',
      selectedBgColorClass: 'bg-white',
      iconBgColorClass: 'bg-white-snow',
      selectedIconBgColorClass: 'bg-green-pure',
      checkIconColor: undefined,
      inputColor: undefined,
    },
    detailsContainer: {
      base: 'bg-white',
      selected: 'bg-white',
      iconBgColorClass: 'bg-white-snow',
      selectedIconBgColorClass: 'bg-green-pure',
    },
    cardIconSize: 'large',
  },
  'wix-mini': {
    countriesDropdown: {
      bgColorClass: 'bg-[#F8F8F8]',
      selectedBgColorClass: 'bg-white-snow',
      iconBgColorClass: 'bg-[#000624]/[0.05]',
      selectedIconBgColorClass: 'bg-primary',
      checkIconColor: 'var(--color-primary)',
      inputColor: '#0006240D',
    },
    detailsContainer: {
      base: 'bg-white-83',
      selected: 'bg-gray-athens',
      iconBgColorClass: 'bg-white-snow',
      selectedIconBgColorClass: 'bg-primary',
    },
    cardIconSize: 'medium',
  },
} as const;

const PrivacyLevelCard = ({
  handlePrivacyLevelClick,
  selected,
  value,
  baseKey,
  options,
  handleOnOptionClick,
  onHover,
  variant = 'default',
  disabled,
  expanded,
  disabledTooltip,
}: DataPrivacyCardProps) => {
  const translate = useTranslate();
  const isContributor = useSelector(contributorRole);
  const isFeatureEnabled = useSelector(isCountryBasedPrivacyModeEnabled);

  const levelFeatures = usePrivacyLevelFeatures(variant);

  const selectedOptions = useMemo(
    () =>
      options.filter((option) => option.status === OptionStatusEnum.active || option.status === OptionStatusEnum.locked)
        .length ?? 0,
    [options],
  );

  const lockedOptionsCount = useMemo(
    () => options.filter((option) => option.status === OptionStatusEnum.locked).length ?? 0,
    [options],
  );

  const upgradeTooltipProps = useMemo(() => {
    return {
      content: is3asApp() || isWixMiniApp() ? <AaasMinCreditsTooltip /> : <UpgradePlanTooltip />,
      tooltipClassNames: 'w-360px',
      interactive: true,
      disabled: isFeatureEnabled,
      useDefaultStyle: false,
    };
  }, [isFeatureEnabled]);

  const infoTooltip = useMemo(() => {
    return (
      <TooltipWrapper {...upgradeTooltipProps}>
        <div className='pulse'>
          <RadioInputSelected color='#F66F1E' className='shrink-0 h-18px w-18px' />
        </div>
      </TooltipWrapper>
    );
  }, [upgradeTooltipProps]);

  const accordionChildTooltipProps = useMemo(() => {
    if (isContributor) {
      return { content: translate('tooltips.contributorRole') };
    }
    if (!isFeatureEnabled) {
      return upgradeTooltipProps;
    }
    return undefined;
  }, [isContributor, translate, isFeatureEnabled, upgradeTooltipProps]);

  return (
    <PrivacyLevelCardWrapper
      onClick={handlePrivacyLevelClick}
      onHover={onHover}
      selected={selected}
      baseKey={baseKey}
      value={value}
      variant={variant}
      disabled={disabled}
      disabledTooltip={disabledTooltip}
    >
      <Paragraph
        colorClassName='text-gray-code'
        className='mt-15px'
        weight={fontWeights.semibold}
        size={variant === 'wix-mini' ? paragraphSizes.medium : paragraphSizes.normal}
      >
        {translate(`all.privacyCenter.privacyLevel.modeActiveFor`)}
      </Paragraph>
      <div className='flex flex-col w-full gap-3 mt-3' onClick={(event) => event.stopPropagation()}>
        {/* z-index set higher than 50 to overlap the "Unidentified Origins" option from the countries dropdown */}
        <DisabledOverlay overlayClassName='z-[51]' enabled={!!disabled} tooltipProps={{ content: disabledTooltip }}>
          <SelectDropdownWithGroups
            id={`privacy-level-countries-dropdown-${value}`}
            isOpen={expanded}
            options={options}
            icon={
              <IconWithBackground
                className={classNames({
                  [styles[variant].countriesDropdown.selectedIconBgColorClass]: selected,
                  [styles[variant].countriesDropdown.iconBgColorClass]: !selected,
                })}
                icon={() => <LocationIcon color={selected ? '#FFFFFF' : '#696969'} />}
                size={styles[variant].cardIconSize}
              />
            }
            titleClassName={variant === 'wix-mini' ? 'text-base' : undefined}
            subtitleClassName={variant === 'wix-mini' ? '!text-14 text-gray-devilSolid' : undefined}
            title={translate('all.privacyCenter.privacyLevel.countriesCount', { count: selectedOptions })}
            subtitle={translate('all.privacyCenter.privacyLevel.lockedCountriesCount', { count: lockedOptionsCount })}
            onOptionClick={(option) => handleOnOptionClick(value, option.value as string)}
            isInitiallyOpen={variant !== 'wix-mini'}
            disabled={isContributor || !isFeatureEnabled || disabled}
            accordionChildTooltipProps={accordionChildTooltipProps}
            infoIcon={!isFeatureEnabled ? infoTooltip : undefined}
            bgColorClass={classNames({
              [styles[variant].countriesDropdown.bgColorClass]: !selected,
              [styles[variant].countriesDropdown.selectedBgColorClass]: selected,
            })}
            checkedIconColor={styles[variant].countriesDropdown.checkIconColor}
            inputColor={!selected ? styles[variant].countriesDropdown.inputColor : undefined}
            data-testid={TEST_IDS.generic.privacyLevelCard.countriesDropdown}
          />
        </DisabledOverlay>
        <PrivacyLevelFeatureCard
          icon={
            <IconWithBackground
              className={classNames({
                [styles[variant].detailsContainer.selectedIconBgColorClass]: selected,
                [styles[variant].detailsContainer.iconBgColorClass]: !selected,
              })}
              icon={() => <FolderIcon color={selected ? '#FFFFFF' : '#696969'} />}
              size={styles[variant].cardIconSize}
            />
          }
          title={translate(`all.privacyCenter.privacyLevel.featureForLevel${value + 1}`)}
          levelFeatures={levelFeatures[value]}
          titleClassName={variant === 'wix-mini' ? 'text-base' : undefined}
          isInitiallyOpen={expanded || variant !== 'wix-mini'}
        />
      </div>
    </PrivacyLevelCardWrapper>
  );
};

export default PrivacyLevelCard;
